/**
 * Top-right user menu — avatar + dropdown.
 *
 * Items:
 *   - User's email (read-only header)
 *   - Sign out
 *
 * Full profile/settings menu items land in later phases.
 */
import { LogOut, Shield } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import { cn } from '../../lib/cn';

export function UserMenu(): JSX.Element | null {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent | TouchEvent) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!user) return null;

  const initials = user.displayName
    .split(/\s+/)
    .map((w) => w[0])
    .slice(0, 2)
    .join('')
    .toUpperCase();

  return (
    <div ref={wrapRef} className="relative">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="User menu"
        className={cn(
          'inline-flex size-8 items-center justify-center rounded-tight bg-accent/10 text-xs font-medium text-accent',
          'transition-colors duration-instant ease-standard hover:bg-accent/15',
        )}
      >
        {initials || '?'}
      </button>

      {open && (
        <div
          role="menu"
          className={
            'absolute right-0 top-full z-50 mt-1 w-56 overflow-hidden rounded-surface border border-rule bg-paper ' +
            'shadow-popover'
          }
        >
          <div className="border-b border-rule bg-paper-soft px-3 py-2">
            <p className="truncate text-sm font-medium text-ink">{user.displayName}</p>
            <p className="truncate text-xs text-ink-muted">{user.email}</p>
          </div>
          <button
            type="button"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              navigate('/account/security');
            }}
            className={
              'flex w-full items-center gap-2 border-b border-rule px-3 py-2 text-left text-sm text-ink ' +
              'transition-colors duration-instant ease-standard hover:bg-paper-soft'
            }
          >
            <Shield className="size-3.5 text-ink-muted" />
            Security & MFA
          </button>
          <button
            type="button"
            role="menuitem"
            onClick={async () => {
              setOpen(false);
              await logout();
              // Imperative redirect — belt-and-suspenders with RequireAuth's
              // unauthenticated check. Without this, RequireAuth only fires
              // on the next render, which can lag behind the click on slower
              // devices and made the UI appear to "do nothing" until refresh.
              navigate('/login', { replace: true });
            }}
            className={
              'flex w-full items-center gap-2 px-3 py-2 text-left text-sm text-ink ' +
              'transition-colors duration-instant ease-standard hover:bg-paper-soft'
            }
          >
            <LogOut className="size-3.5 text-ink-muted" />
            Sign out
          </button>
        </div>
      )}
    </div>
  );
}
